<template>
  <section
    :id="id"
    ref="actualDropTarget"
    :aria-labelledby="`${id}-header`"
    class="consolidated-shipment"
    tabindex="-1"
    @dragenter="onDragEnter"
    @dragleave="onDragLeave"
    @dragover="onDragOver"
    @drop="onDropElement($event, addOrReorderShipOrder)"
  >
    <BlockUI :blocked="isAcceptingRate" full-screen />
    <div
      v-show="draggingEl"
      ref="overlay"
      class="overlay opacity-20 fixed top-0 left-0 w-full h-full"
      @dragenter="onOverlayDragEnter"
      @dragleave="onOverlayDragLeave"
      @drop="onDropElement($event, removeDroppedShipOrder)"
    ></div>

    <Card class="consolidated-shipment-card relative z-1">
      <template #header>
        <h2 :id="`${id}-header`" tabindex="-1">
          <router-link
            v-if="existingShipmentId"
            :to="{
              name: 'shipmentDetail',
              params: { shipmentId: existingShipmentId },
            }"
            class="details-link flex align-items-center"
          >
            {{ t('shipments.shipmentNumParam', { param: existingShipmentId }) }}
          </router-link>
          <template v-else>
            {{ t('common.newParam', { param: t('shipments.shipments') }) }}
          </template>
        </h2>

        <div
          v-if="proposedShipment.origin && proposedShipment.destination"
          class="consolidated-shipment-header-locations flex flex-gap-1 justify-content-between align-items-center w-6"
        >
          <div>
            <NamedAddress
              :name="proposedShipment.origin.location?.locationName"
              :address="proposedShipment.origin.location?.toAddress()"
              suppress-address-lines
              supress-postal-code
            />
            <div>
              {{
                renderDateTime(
                  proposedShipment.origin.requestedAppointmentRange?.start,
                  { timeFormat: 'NONE' },
                )
              }}
            </div>
          </div>
          <FontAwesomeIcon
            aria-hidden="true"
            class="text-gray-darker"
            :icon="['fal', 'long-arrow-right']"
          />
          <div>
            <NamedAddress
              :name="proposedShipment.destination.location?.locationName"
              :address="proposedShipment.destination.location?.toAddress()"
              suppress-address-lines
              supress-postal-code
            />
            <div>
              {{
                renderDateTime(
                  proposedShipment.destination.requestedAppointmentRange?.end,
                  { timeFormat: 'NONE' },
                )
              }}
            </div>
          </div>
        </div>
      </template>

      <template #content>
        <div class="consolidated-shipment-card-content">
          <ConsolidatedShipmentDials
            :num-handling-units="totalHandlingUnits"
            :weight="totalWeight"
            :linear="totalLinear"
            :volume="totalVolume"
            :handling-unit="handlingUnits"
            :weight-uom="WeightUom.POUND"
            :linear-uom="DimsUom.FOOT"
            :volume-uom="VolumeUom.CUBIC_FOOT"
            class="w-10 mx-auto"
          />

          <RygenTabs
            v-model:active-tab-key="activeTabKey"
            :tabs="tabs"
            class="consolidated-shipment-card-tabs"
          >
            <TabPanel value="ORDERS">
              <div class="consolidated-ship-orders-tab">
                <ul role="list" class="consolidated-ship-orders-list w-10">
                  <li
                    v-for="s in proposedShipment.shipOrders"
                    :key="s.id"
                    class="consolidated-ship-order"
                  >
                    <OpenShipOrderCard
                      :ship-order="s"
                      :data-ship-order-id="s.id"
                      class="consolidated-ship-order-card"
                      removable
                      draggable
                      @remove="onRemoveShipOrder"
                      @dragstart="onDragStart($event, setShipOrderId)"
                    />
                  </li>
                </ul>

                <div ref="visualDropTarget" class="drop-target w-10">
                  {{ t('consolidation.dropTargetInstructions') }}
                </div>
              </div>
            </TabPanel>

            <TabPanel value="SHIPMENT_DETAILS">
              <ConsolidatedShipmentDetails
                v-model:proposed-shipment="proposedShipment"
              />
            </TabPanel>
          </RygenTabs>
        </div>

        <RateShop
          v-if="rateShop.data || rateShop.loading"
          id="consolidated-shipment-rate-shop"
          :rate-shop="rateShop"
          :selectable="!areRatesInvalidated && !isAcceptingRate"
          class="w-full"
          @rate-selected="(payload) => createOrUpdateShipment(payload)"
        />
      </template>

      <template #footer>
        <RygenButton
          v-tooltip="getRatesButtonTooltip"
          mode="secondary"
          :disabled="
            loading ||
            !hasShipOrders ||
            isAcceptingRate ||
            rateShop.loading ||
            proposedShipment.isExistingShipment
          "
          :label="t('rateShop.getRates')"
          class="w-10"
          juicy
          @click="getRates"
        />
        <RygenButton
          v-tooltip="{
            value: t('consolidation.addOrderToPerformParam', {
              param: lowerCase(primaryButtonLabel),
            }),
            disabled: hasShipOrders,
          }"
          mode="primary"
          :disabled="!hasShipOrders || isAcceptingRate"
          :label="primaryButtonLabel"
          :loading="loading || isAcceptingRate"
          icon="pi"
          class="w-10"
          juicy
          @click="createOrUpdateShipment()"
        />
        <!-- ^ createOrUpdateShipment() with parens so the click event isn't passed -->
      </template>
    </Card>
  </section>
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRef, toRefs, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import {
  useDropTarget,
  useToast,
  useTooltip,
  useUnsavedChangesConfirmation,
} from '@rygen/rygen-ui/composables';
import { lowerCase, renderDateTime } from '@rygen/rygen-ui/i18n';
import {
  DimsUom,
  Loadable,
  Tab,
  VolumeUom,
  WeightUom,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import { useLineItemsSummary } from '@/composables';
import {
  AcceptQuotePayload,
  AcceptRateQuoteRequest,
  RateShop as RateShopModel,
  ShipOrder,
} from '@/model';
import { ConsolidatedShipment } from '@/model/consolidation';

import RateShop from '@/components/rateShop/RateShop.vue';
import ConsolidatedShipmentDetails from '@/components/shipmentPlanning/consolidation/ConsolidatedShipmentDetails.vue';
import ConsolidatedShipmentDials from '@/components/shipmentPlanning/consolidation/ConsolidatedShipmentDials.vue';
import OpenShipOrderCard from '@/components/shipmentPlanning/consolidation/OpenShipOrderCard.vue';
import NamedAddress from '@rygen/rygen-ui/components/address/NamedAddress.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import RygenTabs from '@rygen/rygen-ui/components/ui/RygenTabs.vue';
import BlockUI from 'primevue/blockui';
import Card from 'primevue/card';
import TabPanel from 'primevue/tabpanel';

type TabKey = 'ORDERS' | 'SHIPMENT_DETAILS';

const proposedShipment = defineModel({
  type: Object as PropType<ConsolidatedShipment>,
  required: true,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  shipOrders: {
    type: Array as PropType<ShipOrder[]>,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
});
const { id, shipOrders, loading } = toRefs(props);

const visualDropTarget = useTemplateRef('visualDropTarget');
const actualDropTarget = useTemplateRef('actualDropTarget');
const overlay = useTemplateRef('overlay');

/**
 * One of the consolidation candidates
 *
 * The dragging element should have a red border. The user can move it to
 * sort the consolidation candidates or drop it outside this component's
 * card to remove that shipOrder.
 */
const draggingEl: Ref<HTMLElement | null> = ref(null);
const lineItems = toRef(() => proposedShipment.value.lineItems);

const { onDragOver, onDragStart, onDropElement } =
  useDropTarget(actualDropTarget);
const { t } = useI18n();
const {
  handlingUnits,
  totalHandlingUnits,
  totalLinear,
  totalVolume,
  totalWeight,
} = useLineItemsSummary(lineItems);
const router = useRouter();
const toast = useToast();
const vTooltip = useTooltip();

const activeTabKey: Ref<TabKey> = ref('ORDERS');
const hasCreatedShipment = ref(false);
useUnsavedChangesConfirmation({
  isDirtyCallback: async () =>
    !!proposedShipment.value.shipOrders.length && !hasCreatedShipment.value,
});

const rateShop: Ref<Loadable<RateShopModel>> = ref({ loading: false });
const areRatesInvalidated = ref(true);
const isAcceptingRate = ref(false);

/**
 * An array of `ShipOrder`s the user has chosen to remove from
 * `proposedShipment`. Only populated if `proposedShipment` is an existing
 * shipment.
 */
const shipOrdersToRemove: Ref<ShipOrder[]> = ref([]);

const tabs = computed((): Tab<TabKey>[] => [
  {
    key: 'ORDERS',
    header: t('orders.orders', 2),
    visible: true,
  },
  {
    key: 'SHIPMENT_DETAILS',
    header: t('common.paramDetails', { param: t('shipments.shipments') }),
    visible: true,
  },
]);

const visibleTabs = computed(() => tabs.value.filter((t) => t.visible));

const primaryButtonLabel = computed(() => {
  return proposedShipment.value.isExistingShipment
    ? t('common.updateParam', { param: t('shipments.shipments') })
    : t('common.createParam', { param: t('shipments.shipments') });
});

const existingShipmentId = computed(() => {
  return proposedShipment.value.shipOrders
    .map((so) => so.shipmentId)
    .find(notNull);
});

const hasShipOrders = computed(() => {
  return proposedShipment.value.shipOrders.length > 0;
});

const getRatesButtonTooltip = computed(() => {
  return proposedShipment.value.isExistingShipment
    ? {
        value: t('consolidation.viewRatesOnDetails'),
      }
    : {
        value: t('consolidation.addOrderToPerformParam', {
          param: lowerCase(t('rateShop.getRates')),
        }),
        disabled: hasShipOrders,
      };
});

const onDragEnter = (event: DragEvent) => {
  if (!event.dataTransfer) {
    console.warn('No dataTransfer object for drag. WTF');
    return;
  }

  // If draggingEl is truthy, the user is dragging a shipOrder from this
  // component and we don't want to highlight the drop zone
  if (draggingEl.value || !event.dataTransfer.types.includes('text/plain')) {
    return;
  }

  if (actualDropTarget.value?.contains(event.target as HTMLElement)) {
    visualDropTarget.value?.classList.add('drag-item-hovering');
    event.preventDefault();
  }
};

const onOverlayDragEnter = (event: DragEvent) => {
  if (!event.dataTransfer) {
    console.warn('No dataTransfer object for drag. WTF');
    return;
  }

  if (!event.dataTransfer.types.includes('text/plain')) {
    return;
  }

  if (overlay.value?.contains(event.target as HTMLElement)) {
    draggingEl.value?.classList?.add('hovering-drop-zone');
    event.preventDefault();
  }
};

const onDragLeave = (event: DragEvent) => {
  if (!event.dataTransfer) {
    console.warn('No dataTransfer object for drag. WTF');
    return;
  }

  if (!actualDropTarget.value?.contains(event.relatedTarget as HTMLElement)) {
    visualDropTarget.value?.classList.remove('drag-item-hovering');
  }
};

const onOverlayDragLeave = (event: DragEvent) => {
  if (!event.dataTransfer) {
    console.warn('No dataTransfer object for drag. WTF');
    return;
  }

  if (!overlay.value?.contains(event.relatedTarget as HTMLElement)) {
    draggingEl.value?.classList?.remove('hovering-drop-zone');
  }
};

const setShipOrderId = async (
  target: HTMLElement,
  dataTransfer: DataTransfer,
) => {
  dataTransfer.dropEffect = 'move';
  dataTransfer.effectAllowed = 'move';

  draggingEl.value = target;
  target.classList.add('dragging');
  if (target.dataset.shipOrderId) {
    dataTransfer.setData('text/plain', target.dataset.shipOrderId);
  }
};

const addOrReorderShipOrder = async (
  _target: HTMLElement,
  dataTransfer: DataTransfer,
) => {
  const data = dataTransfer.getData('text/plain');
  const shipOrderId = Number.parseInt(data, 10);
  const droppedShipOrder = shipOrders.value.find((s) => s.id === shipOrderId);

  if (droppedShipOrder) {
    proposedShipment.value.addShipOrder(droppedShipOrder);
    areRatesInvalidated.value = true;
  }

  // Remove all dragging indicators
  visualDropTarget.value?.classList.remove('drag-item-hovering');
  draggingEl.value?.classList?.remove('dragging');
  draggingEl.value = null;
};

const removeDroppedShipOrder = async (
  _target: HTMLElement,
  dataTransfer: DataTransfer,
) => {
  // A drop on the overlay means the user is trying to remove a shipOrder
  const data = dataTransfer.getData('text/plain');
  const shipOrderId = Number.parseInt(data, 10);
  const droppedShipOrder = shipOrders.value.find((s) => s.id === shipOrderId);

  if (droppedShipOrder) {
    onRemoveShipOrder(droppedShipOrder);
  }

  // Remove all dragging indicators
  draggingEl.value?.classList?.remove('dragging');
  draggingEl.value = null;
};

const onRemoveShipOrder = (shipOrder: ShipOrder) => {
  proposedShipment.value.removeShipOrder(shipOrder);
  areRatesInvalidated.value = true;
  if (proposedShipment.value.isExistingShipment) {
    shipOrdersToRemove.value.push(shipOrder);
  }
};

const getRates = async (): Promise<void> => {
  try {
    rateShop.value.loading = true;
    rateShop.value.data = await api.shipmentPlanningConsolidation.getRates(
      proposedShipment.value.toJSON(),
    );
    areRatesInvalidated.value = false;
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    rateShop.value.data = new RateShopModel();
    console.error('error getting rates', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('rateShop.rateShop'),
      }),
      error: e,
    });
  } finally {
    rateShop.value.loading = false;
  }
};

const createOrUpdateShipment = (payload: Partial<AcceptQuotePayload> = {}) => {
  if (proposedShipment.value.isExistingShipment) {
    updateShipment(payload);
  } else {
    createShipment(payload);
  }
};

const createShipment = async ({
  rateQuote,
  rateSelectionReason,
}: Partial<AcceptQuotePayload>) => {
  try {
    isAcceptingRate.value = true;
    const { shipmentId } =
      await api.shipmentPlanningConsolidation.createConsolidatedShipment(
        proposedShipment.value.toJSON(),
        rateQuote,
        rateSelectionReason,
      );
    hasCreatedShipment.value = true;
    router
      .push({
        name: 'shipmentDetail',
        params: {
          shipmentId: `${shipmentId}`,
        },
      })
      .then(() =>
        toast.addSuccess({
          detail: t('messages.successSavingParam', {
            param: t('shipments.shipments'),
          }),
        }),
      );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error creating shipment', e);
    toast.addError({
      detail: t('errors.savingParam', {
        param: t('shipments.shipments'),
      }),
      error: e,
    });
  } finally {
    isAcceptingRate.value = false;
  }
};

const updateShipment = async ({
  rateQuote,
  rateSelectionReason,
}: Partial<AcceptQuotePayload>) => {
  if (!proposedShipment.value.isExistingShipment || !existingShipmentId.value) {
    throw new Error("Can't update a shipment that doesn't exist yet");
  }

  try {
    isAcceptingRate.value = true;

    const shipOrderIdsToAdd = proposedShipment.value.shipOrders
      .filter((so) => !so.shipmentId)
      .map((so) => so.id)
      .filter(notNull);
    const shipOrderIdsToRemove = shipOrdersToRemove.value
      .map((so) => so.id)
      .filter(notNull);

    await api.shipmentPlanningConsolidation.modifyShipOrders(
      existingShipmentId.value,
      shipOrderIdsToAdd,
      shipOrderIdsToRemove,
    );
    hasCreatedShipment.value = true;

    const request = new AcceptRateQuoteRequest({
      rateQuoteId: rateQuote?.id,
      rateSelectionReason,
    });

    if (rateQuote?.id) {
      await api.shipment.acceptRateQuote(existingShipmentId.value, request);
    }

    router
      .push({
        name: 'shipmentDetail',
        params: {
          shipmentId: `${existingShipmentId.value}`,
        },
      })
      .then(() =>
        toast.addSuccess({
          detail: t('messages.successUpdatingParam', {
            param: t('shipments.shipments'),
          }),
        }),
      );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error updating shipment', e);
    toast.addError({
      detail: t('errors.updatingParam', {
        param: t('shipments.shipments'),
      }),
      error: e,
    });
  } finally {
    isAcceptingRate.value = false;
  }
};

watch(visibleTabs, (newVal) => {
  activeTabKey.value = newVal[0].key;
});
</script>

<style>
:root {
  --consolidated-shipment-section-header-height: 101.5px;
}

.consolidated-shipment {
  --p-card-body-gap: 0;
  --p-tabs-tabpanel-padding: var(--sp-6) 0 0;

  height: 100%;

  .consolidated-shipment-card.p-card {
    .p-card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--sp-6);
      border-bottom: var(--divider);
      height: var(--consolidated-shipment-section-header-height);

      h2 {
        font-size: 14px;
        font-weight: 700;
      }
    }

    .consolidated-shipment-header-locations {
      .named-address {
        max-width: 200px;
      }
    }

    & > .p-card-body {
      padding: 0;

      .p-card-content {
        .consolidated-shipment-card-content {
          padding-block: var(--sp-6);

          .consolidated-shipment-meter {
            margin-block: var(--sp-6);
          }

          .consolidated-shipment-card-tabs {
            width: 100%;
            padding-inline: var(--sp-6);

            .p-tabpanels {
              .p-tabpanel {
                /*
                About the height of the card with 3 ship orders added to prevent the user
                having to scroll down to far for the buttons but still have more than a
                single ship order visible at a time
                */
                max-height: 500px;
                overflow: auto;

                .consolidated-ship-orders-tab {
                  display: flex;
                  flex-flow: column;
                  justify-content: center;
                  align-items: center;
                  gap: var(--sp-3);

                  .consolidated-ship-orders-list {
                    list-style: none;
                    margin: 0;
                    padding: 0;
                    height: 100%;

                    .consolidated-ship-order {
                      & + .consolidated-ship-order {
                        margin-top: var(--sp-3);
                      }

                      .consolidated-ship-order-card.p-card {
                        &.dragging {
                          border-color: var(--p-blue-500);
                          cursor: grabbing;
                          opacity: 0.5;

                          &.hovering-drop-zone {
                            border-color: var(--danger-color);
                          }
                        }
                      }
                    }
                  }

                  .drop-target {
                    display: flex;
                    justify-content: center;
                    align-items: center;

                    background-color: var(--p-gray-50);
                    border: 2px dashed var(--p-gray-300);
                    color: var(--p-gray-400);
                    font-size: 14px;
                    font-weight: 500;
                    padding: var(--sp-11);
                    position: sticky;
                    bottom: 0;

                    &.drag-item-hovering {
                      border-color: var(--p-blue-500);
                    }
                  }
                }
              }
            }
          }
        }

        .rate-shop {
          border-top: var(--divider);
          margin-top: var(--sp-3);
          padding: var(--sp-6) var(--sp-6) 0;
        }
      }

      .p-card-footer {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-flow: column;
        gap: var(--sp-3);
        padding: var(--sp-6);
        border-top: var(--divider);
      }
    }
  }

  .overlay {
    background: black;
  }
}
</style>
